import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaTools } from 'react-icons/fa';
import { useTitle } from '../../../context/TitleContext';
import { showErrorToast, showSuccessToast } from '../../../shared/utils/toastHelper';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import {
  fetchTechnicians,
  addTechnician,
  updateTechnician,
  deleteTechnician,
  clearError,
  clearSuccess,
} from '../../../redux/Slices/AdminTechniciansSlice';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const initialFormState = {
  name: '',
  phone: '',
  job_ar: '',
  job_en: '',
  image: null,
  imagePreview: '',
};

const AdminTechnicians = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();

  const { technicians, pagination, loading, saving, deleting, error, success } = useSelector(
    (state) => state.adminTechnicians || {}
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState('add');
  const [formState, setFormState] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });
  const [imageLoading, setImageLoading] = useState(false);

  useEffect(() => {
    setTitle('الفنيين');
  }, [setTitle]);

  useEffect(() => {
    dispatch(fetchTechnicians({ page: currentPage, search: appliedSearch }));
  }, [dispatch, currentPage, appliedSearch]);

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      showSuccessToast(success);
      dispatch(clearSuccess());
      dispatch(fetchTechnicians({ page: currentPage, search: appliedSearch }));
    }
  }, [success, dispatch, currentPage, appliedSearch]);

  const filteredTechnicians = useMemo(() => technicians || [], [technicians]);

  const resolveImageUrl = (value) => {
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    const trimmedApi = BASE_URL.replace(/\/api$/i, '');
    const base = trimmedApi.endsWith('/') ? trimmedApi.slice(0, -1) : trimmedApi;
    const path = value.startsWith('/') ? value : `/${value}`;
    return `${base}${path}`;
  };

  const openAddModal = () => {
    setFormMode('add');
    setFormState(initialFormState);
    setEditingId(null);
    setImageLoading(false);
    setShowFormModal(true);
  };

  const openEditModal = (technician) => {
    if (!technician) return;
    setFormMode('edit');
    setEditingId(technician.id);
    const existingImageUrl = resolveImageUrl(technician.image_url || technician.image || '');
    setFormState({
      name: technician.name || '',
      phone: technician.phone || '',
      job_ar: technician.job_ar || '',
      job_en: technician.job_en || '',
      image: existingImageUrl ? 'existing' : null, // Mark existing image
      imagePreview: existingImageUrl,
    });
    setImageLoading(false);
    setShowFormModal(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setImageLoading(false);
      return;
    }
    
    setImageLoading(true);
    const previewUrl = URL.createObjectURL(file);
    
    // Wait for image to load
    const img = new Image();
    img.onload = () => {
      setFormState((prev) => ({ ...prev, image: file, imagePreview: previewUrl }));
      setImageLoading(false);
    };
    img.onerror = () => {
      setImageLoading(false);
      showErrorToast('فشل في تحميل الصورة');
    };
    img.src = previewUrl;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const { name, phone, job_ar, job_en, image } = formState;

    if (!name || !phone || !job_ar || !job_en) {
      showErrorToast('يرجى إدخال جميع الحقول المطلوبة');
      return;
    }

    // For edit mode, image can be existing or new
    if (formMode === 'add' && !image) {
      showErrorToast('يرجى إدخال جميع الحقول المطلوبة بما فيها الصورة');
      return;
    }

    if (imageLoading) {
      showErrorToast('جاري تحميل الصورة، يرجى الانتظار...');
      return;
    }

    const payload = new FormData();
    payload.append('name', name);
    payload.append('phone', phone);
    payload.append('job_ar', job_ar);
    payload.append('job_en', job_en);
    // Only append image if it's a File object (not 'existing' string)
    if (image && image !== 'existing' && image instanceof File) {
      payload.append('image', image);
    }

    try {
      if (formMode === 'add') {
        await dispatch(addTechnician(payload)).unwrap();
      } else if (formMode === 'edit' && editingId) {
        await dispatch(updateTechnician({ id: editingId, formData: payload })).unwrap();
      }
      setShowFormModal(false);
      setFormState(initialFormState);
    } catch (e) {
      // error handled via slice
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete.id) return;
    try {
      await dispatch(deleteTechnician(confirmDelete.id)).unwrap();
    } catch (e) {
      // error handled via slice
    } finally {
      setConfirmDelete({ open: false, id: null });
    }
  };

  const renderPagination = () => {
    const totalPages = pagination?.pages_count || pagination?.last_page || 1;
    const current = pagination?.current_page || currentPage;

    if (totalPages < 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i += 1) {
      pages.push(
        <li key={i} className={`page-item ${current === i ? 'active' : ''}`}>
          <button className="page-link" onClick={() => setCurrentPage(i)}>
            {i}
          </button>
        </li>
      );
    }

    return (
      <nav aria-label="Technicians pagination">
        <ul className="pagination justify-content-center">
          <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={current === 1}
            >
              السابق
            </button>
          </li>
          {pages}
          <li className={`page-item ${current === totalPages ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => setCurrentPage(totalPages)}
              disabled={current === totalPages}
            >
              التالي
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  return (
    <div className="main_content">
      <div className="dashboard-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0 d-flex align-items-center gap-2">
            <FaTools />
            <span>الفنيين</span>
          </h4>
          <button className="btn text-white" style={{ backgroundColor: 'var(--main-color)' }} onClick={openAddModal}>
            <FaPlus className="ms-2" /> إضافة فني
          </button>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-9">
                <label className="form-label">بحث بالاسم أو الهاتف</label>
                <div className="input-group">
                  <input
                    className="form-control"
                    placeholder="اكتب للبحث..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setAppliedSearch(searchInput.trim());
                        setCurrentPage(1);
                      }
                    }}
                  />
                  <span className="input-group-text bg-light">
                    <FaSearch />
                  </span>
                </div>
              </div>
              <div className="col-md-3 d-flex">
                <button
                  className="btn text-white w-100"
                  style={{ backgroundColor: 'rgb(24, 65, 148)' }}
                  onClick={() => {
                    setAppliedSearch(searchInput.trim());
                    setCurrentPage(1);
                  }}
                >
                  <FaSearch className="ms-2" /> بحث
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-lg border-0">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">جاري تحميل الفنيين...</p>
              </div>
            ) : filteredTechnicians.length === 0 ? (
              <div className="text-center py-5 text-muted">لا يوجد فنيون</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-modern">
                  <thead>
                    <tr>
                      <th className="text-center">#</th>
                      <th className="text-center">الاسم</th>
                      <th className="text-center">الصورة</th>
                      <th className="text-center">الهاتف</th>
                      <th className="text-center">الوظيفة (ع)</th>
                      <th className="text-center">الوظيفة (EN)</th>
                      <th className="text-center">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTechnicians.map((tech, index) => (
                      <tr key={tech.id || index}>
                        <td className="text-center">
                          <div className="order-number-badge">#{tech.id ?? '-'}</div>
                        </td>
                        <td className="text-center">{tech.name || '-'}</td>
                        <td className="text-center">
                          {tech.image_url || tech.image ? (
                            <img
                              src={resolveImageUrl(tech.image_url || tech.image)}
                              alt={tech.name || 'tech'}
                              style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }}
                            />
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="text-center">{tech.phone || '-'}</td>
                        <td className="text-center">{tech.job_ar || '-'}</td>
                        <td className="text-center">{tech.job_en || '-'}</td>
                        <td className="text-center" style={{ minWidth: '220px' }}>
                          <div className="d-flex justify-content-center align-items-center gap-2 flex-nowrap" style={{ flexWrap: 'nowrap' }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => openEditModal(tech)}>
                              <FaEdit className="ms-1" /> تعديل
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => setConfirmDelete({ open: true, id: tech.id })}
                              disabled={deleting && confirmDelete.id === tech.id}
                            >
                              <FaTrash className="ms-1" /> {deleting && confirmDelete.id === tech.id ? 'جار الحذف...' : 'حذف'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-3">{renderPagination()}</div>
          </div>
        </div>
      </div>

      {showFormModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '600px' }}>
            <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div className="modal-header" style={{ background: 'linear-gradient(135deg, var(--main-color) 0%, #764ba2 100%)', color: 'white' }}>
                <h5 className="modal-title mb-0">{formMode === 'add' ? 'إضافة فني' : 'تعديل فني'}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowFormModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">الاسم</label>
                      <input
                        className="form-control"
                        value={formState.name}
                        onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
                        placeholder="اسم الفني"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">رقم الهاتف</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={formState.phone}
                        onChange={(e) => setFormState((prev) => ({ ...prev, phone: e.target.value }))}
                        placeholder="رقم الهاتف"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">المسمى الوظيفي (عربي)</label>
                      <input
                        className="form-control"
                        value={formState.job_ar}
                        onChange={(e) => setFormState((prev) => ({ ...prev, job_ar: e.target.value }))}
                        placeholder="مثال: فني صيانة"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">المسمى الوظيفي (English)</label>
                      <input
                        className="form-control"
                        value={formState.job_en}
                        onChange={(e) => setFormState((prev) => ({ ...prev, job_en: e.target.value }))}
                        placeholder="e.g. Maintenance Technician"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">الصورة</label>
                      <input type="file" className="form-control" accept="image/*" onChange={handleFileChange} />
                      {formState.imagePreview && (
                        <div className="mt-2 text-center">
                          <img
                            src={formState.imagePreview}
                            alt="preview"
                            style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '12px' }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer d-flex justify-content-center gap-2">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowFormModal(false)}>
                    إلغاء
                  </button>
                  <button 
                    type="submit" 
                    className="btn text-white" 
                    style={{ backgroundColor: 'var(--main-color)' }} 
                    disabled={saving || imageLoading || (formMode === 'add' && !formState.image)}>
                    {saving ? 'جار الحفظ...' : imageLoading ? 'جاري تحميل الصورة...' : formMode === 'add' ? 'إضافة' : 'تحديث'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {confirmDelete.open && (
        <ConfirmModal
          isOpen={confirmDelete.open}
          title="تأكيد الحذف"
          message="هل أنت متأكد من حذف هذا الفني؟"
          confirmText="حذف"
          confirmButtonColor="#dc3545"
          cancelText="إلغاء"
          onClose={() => setConfirmDelete({ open: false, id: null })}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
};

export default AdminTechnicians;
